import { useCallback, useMemo, useState } from 'react';

export interface FieldValidationOptions {
    required?: boolean;
    customValidator?: (value: any) => boolean;
    errorMessage?: string;
}

export interface UseFieldValidationReturn {
    isValid: boolean;
    errorMessage: string | null;
    handleChange: (value: any, originalOnChange?: (value: any, actionMeta?: any) => void) => void;
    validate: (value: any) => boolean;
    setIsValid: (valid: boolean) => void;
}

export const useFieldValidation = (
    options: FieldValidationOptions = {},
): UseFieldValidationReturn => {
    const { required = false, customValidator, errorMessage = '' } = options;

    const [isValid, setIsValid] = useState<boolean>(true);

    const validate = useCallback(
        (value: any): boolean => {
            if (!required && (value === null || value === undefined || value === '')) {
                return true;
            }

            if (required) {
                const hasValue =
                    value !== null &&
                    value !== undefined &&
                    value !== '' &&
                    (Array.isArray(value) ? value.length > 0 : true);

                if (!hasValue) {
                    return false;
                }
            }

            if (customValidator) {
                return customValidator(value);
            }

            return true;
        },
        [required, customValidator],
    );

    const handleChange = useCallback(
        (value: any, originalOnChange?: (value: any, actionMeta?: any) => void) => {
            const isValueValid = validate(value);
            setIsValid(isValueValid);

            if (originalOnChange) {
                originalOnChange(value, {});
            }
        },
        [validate],
    );

    const currentErrorMessage = useMemo(
        () => (!isValid ? errorMessage : null),
        [isValid, errorMessage],
    );

    return {
        isValid,
        errorMessage: currentErrorMessage,
        handleChange,
        validate,
        setIsValid,
    };
};
